import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Tooltip from '~/components/Tooltip'
import { getChainName } from '~/utils/chain'
import styled from 'styled-components'
import { Chain } from '~/typings/tokenlon'
import { FlexContainer } from '~/components/Layout'

interface NetworkListBoxProps {
  networks: Chain[]
  selectedChainId: number | null
  handleSelectNetwork: (chainId: number | null) => void
  handleDisplayMore?: (display: boolean) => void
}

const MAX_DISPLAY_NETWORK_LENGTH = 5

const NetworkListBox: React.FC<NetworkListBoxProps> = ({
  networks,
  selectedChainId,
  handleSelectNetwork,
  handleDisplayMore,
}) => {
  const { t } = useTranslation()
  const displayNetworks = networks.slice(0, MAX_DISPLAY_NETWORK_LENGTH)

  const selectedNetworkLabel = useMemo(() => {
    const isSelectedAll = selectedChainId === null
    if (isSelectedAll) return t('all')
    return getChainName(selectedChainId)
  }, [selectedChainId])

  return (
    <StyledContainer>
      <StyledLabelWrapper>
        <span>{t('network')}</span>
        <StyledNetworkLabel>{selectedNetworkLabel}</StyledNetworkLabel>
      </StyledLabelWrapper>

      <StyledNetworksContainer gap={8} align="center">
        <StyledNetworkItem
          isSelected={selectedChainId === null}
          onClick={() => handleSelectNetwork(null)}
        >
          <span>{t('all')}</span>
        </StyledNetworkItem>
        {displayNetworks.map((network) => (
          <Tooltip
            placement="top"
            mobilePlacement="top"
            title={getChainName(network.chainId)}
            trigger="hover"
            key={network.chainId}
          >
            <StyledNetworkItem
              isSelected={selectedChainId === network.chainId}
              onClick={() => handleSelectNetwork(network.chainId)}
            >
              <img
                src={network.logoUrl}
                alt={network.chainName}
                className="logo"
              />
            </StyledNetworkItem>
          </Tooltip>
        ))}

        {handleDisplayMore && (
          <StyledNetworkItem onClick={() => handleDisplayMore(true)}>
            <span>•••</span>
          </StyledNetworkItem>
        )}
      </StyledNetworksContainer>
    </StyledContainer>
  )
}

export default NetworkListBox

const StyledContainer = styled.div`
  padding: 0 20px;
`

const StyledLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 400;
  color: #b9bed3;
`

const StyledNetworkLabel = styled.span`
  margin-left: 8px;
  color: ${({ theme }) => theme.colors.textStrong};
`

const StyledNetworksContainer = styled(FlexContainer)`
  overflow-x: auto;
`

const StyledNetworkItem = styled.button<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  border: ${({ isSelected, theme }) =>
    isSelected ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.borderSubtle}`};
  background-color: ${({ theme }) => theme.colors.bgCard};
  cursor: pointer;
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);

  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textStrong};

  &:hover {
    background-color: ${({ theme }) => theme.colors.bgSurface};
  }

  ${({ theme }) => theme.media.sm`
    width: 40px;
    height: 40px;

    font-size: 12px;
  `}
`
